import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { todoStream } from '../../state/todo/stream';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { groupStream } from '../../state/group/stream.js';
import { fromAsyncIterable } from '../../../../common/src/lib/signal-from-destroy-ref.js';
import { map, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { StreamInterface } from '@iwentsourcing/core/stream/stream-interface.js';
import { Todo, TodoState } from '../../state/todo/state.js';
import { TodoEvents } from '../../state/todo/events.js';
import { personalTodoAggregation } from '../../state/aggregation/personal-todo.js';
import { AggregateInterface } from '@iwentsourcing/core/aggregation/aggregate-interface.js';
import { timedSignal } from '@iwentsourcing/core/utils/timedSignal.js';
import { BrowseResult } from '@iwentsourcing/core/storage/browse-result.js';
import { ApiService } from '../../../../common/src/lib/api.service.js';
import { AuthConfig } from '../../../../common/src/lib/provide-api.js';
import { createRemoteStream } from '@iwentsourcing/client/management/remote-stream.js';
import { TodoSidebarComponent } from '../todo-sidebar/todo-sidebar.component.js';
import { TodoListComponent } from '../todo-list/todo-list.component.js';
import { TodoListCreateComponent } from '../todo-list-create/todo-list-create.component.js';
import { TodoListItemComponent } from '../todo-list-item/todo-list-item.component.js';
import { AsyncPipe } from '@angular/common';

type Stream =
  | { type: 'stream'; interface: StreamInterface<TodoState, TodoEvents> }
  | { type: 'aggregation'; interface: AggregateInterface<TodoState> };

@Component({
  selector: 'app-todo-page',
  templateUrl: './todo-page.component.html',
  styleUrls: ['./todo-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TodoSidebarComponent,
    TodoListComponent,
    TodoListCreateComponent,
    TodoListItemComponent,
    AsyncPipe,
  ],
})
export class TodoPageComponent {
  api = inject(ApiService);
  authConfig = inject(AuthConfig);
  route = inject(ActivatedRoute);
  destroyRef = inject(DestroyRef);

  apiState = this.api.create(this.authConfig.tenant, this.authConfig.realm);

  stream$ = this.route.params.pipe(
    map<Params, Stream>((params) => {
      if (!params || params['group'] === 'personal') {
        return {
          type: 'stream',
          interface: this.apiState.remote.getStream(
            createRemoteStream(todoStream),
            {},
          ),
        };
      } else if (params['group'] === 'all') {
        return {
          type: 'aggregation',
          interface: this.apiState.remote.getAggregation(
            personalTodoAggregation,
            {},
          ) as any,
        };
      } else {
        return {
          type: 'stream',
          interface: this.apiState.remote.getStream(
            createRemoteStream(groupStream),
            {
              args: {
                groupId: params['group'],
              },
            },
          ) as any,
        };
      }
    }),
  );
  stream = toSignal(this.stream$);

  todos$: Observable<BrowseResult<Todo>> = this.stream$.pipe(
    switchMap((stream) =>
      fromAsyncIterable((signal) =>
        stream.interface.observe('todos').index('date').observeRange(null, {
          take: 10,
          dir: 'prev',
          signal,
        }),
      ),
    ),
    tap((d) => console.log('todos', d)),
    takeUntilDestroyed(this.destroyRef),
    shareReplay(1),
  );

  todos = toSignal(this.todos$);

  async checkedChanged(stream: Stream, evt: { todo: Todo; checked: boolean }) {
    if (stream.type === 'aggregation') {
      return;
    }

    if (evt.checked) {
      await stream.interface.push(
        'complete-todo',
        { id: evt.todo.id },
        { signal: timedSignal() },
      );
    } else {
      await stream.interface.push(
        'incomplete-todo',
        { id: evt.todo.id },
        { signal: timedSignal() },
      );
    }
  }
}
